import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControlLabel,
  Typography,
  Grid,
  Checkbox,
  TextField,
  Container,
  Badge,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { IOSSwitch } from '../IOSSwitch';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

interface RouterDDosMitigationEditProps {
  onSubmit: () => void;
  cpeId: string;
  dDosEnabled: boolean;
}

interface DosThreshold {
  alert_threshold: number;
  block_threshold: number;
  max_threshold?: number;
};

interface DenialOfServiceThresholdsResponse {
  flood_tcp_syn: DosThreshold;
  flood_udp: DosThreshold;
  flood_icmp?: DosThreshold;
}

// const thresholdLimits = {
//   tcpAlert: { min: 100, max: 300 },
//   tcpBlock: { min: 2000, max: 4000 },
//   tcpMax:   { min: 5000, max: 7000 },
//   udpAlert: { min: 1000,  max: 3000 },
//   udpBlock: { min: 5000, max: 8000 },
//   icmpAlert: { min: 100, max: 300 },
//   icmpBlock: { min: 3000, max: 5000 },
// };

// const validateField = (value: string, field: keyof typeof thresholdLimits): string | null => {
//   const num = Number(value);
//   const { min, max } = thresholdLimits[field];

//   if (isNaN(num)) return `must be a valid number`;
//   if (num < min || num > max) return `must be between ${min} and ${max}`;
//   return null;
// };

export const RouterDDosMitigationEdit = ({ onSubmit, cpeId, dDosEnabled }: RouterDDosMitigationEditProps) => {
  const api = useApi();
  const [newDDosEnabled, setNewDDosEnabled] = useState<boolean>(dDosEnabled);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const [advancedEnabled, setAdvancedEnabled] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [originalData, setOriginalData] = useState<any>(null);

  const { data, refetch, isFetching, isSuccess } = useQuery(
    ['getCpeDenialThresholdsData'],
    () => api.getCpeDenialOfServiceThresholdsApiV1GetCpeDenialOfServiceThresholdsGet({ cpe_id: cpeId }),
    {
      enabled: advancedEnabled,
      retry: false,
    }
  );

  const [tcpAlert, setTCPAlert] = useState<string>('');
  const [tcpBlock, setTCPBlock] = useState<string>('');
  const [tcpMax, setTCPMax] = useState<string>('');
  const [udpAlert, setUDPAlert] = useState<string>('');
  const [udpBlock, setUDPBlock] = useState<string>('');
  const [icmpAlert, setICMPAlert] = useState<string>('');
  const [icmpBlock, setICMPBlock] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDDosEnabled(e.target.checked);
    setDisableSubmit(e.target.checked === dDosEnabled);
  };

  const editDDos = useMutation(api.toggleCpeDosApiV1ToggleCpeDenialOfServicePost, {
    onSuccess() {
      toast.success(`${newDDosEnabled ? 'Enabled' : 'Disabled'} DDoS mitigation successfully.`);
      onSubmit();
    },
    onError: (error) => {
      apiError(error);
      setDisableSubmit(false);
    },
  });

  const setDenialServiceThresholds = useMutation(
    (input: { cpe_id: string; data: any }) =>
      api.setCpeDenialOfServiceThresholdsApiV1SetCpeDenialOfServiceThresholdsPost(
        { cpe_id: input.cpe_id },
        input.data
      ),
      {
        onSuccess() {
          onSubmit();
        },
        onError: apiError,
      }
  );

  useEffect(() => {
    if (isSuccess && data?.data && !originalData) {
      const { flood_tcp_syn, flood_udp, flood_icmp } = data.data as DenialOfServiceThresholdsResponse;

      const initialValues = {
        dDosEnabled,
        advancedEnabled: false,
        tcpAlert: flood_tcp_syn?.alert_threshold?.toString() || '200',
        tcpBlock: flood_tcp_syn?.block_threshold?.toString() || '2000',
        tcpMax: flood_tcp_syn?.max_threshold?.toString() || '5000',
        udpAlert: flood_udp?.alert_threshold?.toString() || '1000',
        udpBlock: flood_udp?.block_threshold?.toString() || '5000',
        icmpAlert: flood_icmp?.alert_threshold?.toString() || '100',
        icmpBlock: flood_icmp?.block_threshold?.toString() || '3000',
      };

      setOriginalData(initialValues);

      setTCPAlert(initialValues.tcpAlert);
      setTCPBlock(initialValues.tcpBlock);
      setTCPMax(initialValues.tcpMax);
      setUDPAlert(initialValues.udpAlert);
      setUDPBlock(initialValues.udpBlock);
      setICMPAlert(initialValues.icmpAlert);
      setICMPBlock(initialValues.icmpBlock);
    }
  }, [isSuccess, data, originalData, dDosEnabled]);

  useEffect(() => {
    if (!originalData) return;

    const checkValueChanged =
      newDDosEnabled !== originalData.dDosEnabled ||
      advancedEnabled !== originalData.advancedEnabled ||
      tcpAlert !== originalData.tcpAlert ||
      tcpBlock !== originalData.tcpBlock ||
      tcpMax !== originalData.tcpMax ||
      udpAlert !== originalData.udpAlert ||
      udpBlock !== originalData.udpBlock ||
      icmpAlert !== originalData.icmpAlert ||
      icmpBlock !== originalData.icmpBlock;

    setDisableSubmit(!checkValueChanged);
  }, [
    newDDosEnabled,
    advancedEnabled,
    tcpAlert,
    tcpBlock,
    tcpMax,
    udpAlert,
    udpBlock,
    icmpAlert,
    icmpBlock,
    originalData
  ]);

  const handleShowConfirmDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleSubmit = () => {
    // const validations: { field: keyof typeof thresholdLimits; value: string; label: string }[] = [
    //   { field: 'tcpAlert', value: tcpAlert, label: 'TCP Alert Threshold' },
    //   { field: 'tcpBlock', value: tcpBlock, label: 'TCP Block Threshold' },
    //   { field: 'tcpMax',   value: tcpMax,   label: 'TCP Max Threshold' },
    //   { field: 'udpAlert', value: udpAlert, label: 'UDP Alert Threshold' },
    //   { field: 'udpBlock', value: udpBlock, label: 'UDP Block Threshold' },
    //   { field: 'icmpAlert', value: icmpAlert, label: 'ICMP Alert Threshold' },
    //   { field: 'icmpBlock', value: icmpBlock, label: 'ICMP Block Threshold' },
    // ];

    // for (const { field, value, label } of validations) {
    //   const error = validateField(value, field);
    //   if (error) {
    //     toast.error(`${label} ${error}`);
    //     return;
    //   }
    // }

    setDisableSubmit(true);
    editDDos.mutate({
      cpe_id: cpeId,
      enabled: newDDosEnabled || false
    });

    if (newDDosEnabled && advancedEnabled) {
      setDenialServiceThresholds.mutate({
        cpe_id: cpeId,
        data: {
          tcp_syn_flood_thresholds: {
            alert_threshold: Number(tcpAlert),
            block_threshold: Number(tcpBlock),
            max_threshold: Number(tcpMax),
          },
          udp_thresholds: {
            alert_threshold: Number(udpAlert),
            block_threshold: Number(udpBlock),
            max_threshold: Number(udpBlock),
          },
          icmp_thresholds: {
            alert_threshold: Number(icmpAlert),
            block_threshold: Number(icmpBlock),
            max_threshold: Number(icmpBlock),
          },
        },
      });
    }
  };

  return (
    <Box pt={2}>
      <Box>
        <Typography variant="body1" sx={{ mb: 6 }}>
          Enable or disable DDoS mitigation.
          <Badge badgeContent={0} color="secondary">
            <Box
              component="a"
              href="https://www.bumblebeenet.com/post/bumblebee-ddos-protection-suite"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Tooltip arrow title="Info">
                <IconButton color="primary" size="small">
                  <InfoOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Badge>
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControlLabel
          value="top"
          control={
            <IOSSwitch color="primary" checked={newDDosEnabled} onChange={handleChange} />
          }
          label={newDDosEnabled ? 'Enabled' : 'Disabled'}
          labelPlacement="bottom"
          sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
          componentsProps={{
            typography: {
              sx: (theme) => ({
                fontSize: 12,
                color: !newDDosEnabled ? theme.palette.grey[600] : theme.palette.primary.main,
              }),
            },
          }}
        />
      </Box>
      {newDDosEnabled && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={advancedEnabled}
                onChange={(e) => setAdvancedEnabled(e.target.checked)}
              />
            }
            label="Advanced"
          />
        </Grid>
      )}
      {newDDosEnabled && advancedEnabled && (
        <Grid sx={{margin: 8}}>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mt: 8 }}>TCP SYN flood</Typography>
            <Container>
              <Grid container spacing={5} sx={{ marginTop: 1 }}>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Alert threshold"
                    variant="outlined"
                    value={tcpAlert}
                    onChange={(e) => setTCPAlert(e.target.value)}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Block threshold"
                    variant="outlined"
                    value={tcpBlock}
                    onChange={(e) => setTCPBlock(e.target.value)}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Max threshold"
                    variant="outlined"
                    value={tcpMax}
                    onChange={(e) => setTCPMax(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mt: 12 }}>UDP flood</Typography>
            <Container>
              <Grid container spacing={5} sx={{ marginTop: 1 }}>
                <Grid item md={6}>
                  <TextField
                  fullWidth
                  label="Alert threshold"
                  variant="outlined"
                  value={udpAlert}
                  onChange={(e) => setUDPAlert(e.target.value)}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Block threshold"
                    variant="outlined"
                    value={udpBlock}
                    onChange={(e) => setUDPBlock(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mt: 12 }}>ICMP flood</Typography>
            <Container>
              <Grid container spacing={5} sx={{ marginTop: 1 }}>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Alert threshold"
                    variant="outlined"
                    value={icmpAlert}
                    onChange={(e) => setICMPAlert(e.target.value)}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    label="Block threshold"
                    variant="outlined"
                    value={icmpBlock}
                    onChange={(e) => setICMPBlock(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Grid>
      )}
      {showConfirmDialog && (
        <DialogConfirm
          open={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          handleConfirm={handleSubmit}
          title="Confirmation"
          description="Are you certain you wish to proceed to confirm?"
        />
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleShowConfirmDialog} disabled={disableSubmit}>
          Confirm
        </Button>
      </Box>
    </Box>
  );
};
